import * as fmt from '../../lib/format';
import { PlanData, PlanGroup, PlanRow, PlanStatus } from './types';

export const GROUP_PAGE = 25;

/** Whether this person may change this row: leaders and admins anywhere, an owner for their own project. */
export function canEditRow(can: PlanData['can_edit'], row: Pick<PlanRow, 'project_key'>): boolean {
  return can.all || (row.project_key !== null && can.projects.includes(row.project_key));
}

export function countByStatus(rows: PlanRow[]): Record<PlanStatus, number> {
  const out: Record<PlanStatus, number> = { idea: 0, picked: 0, in_progress: 0, on_hold: 0, live: 0, dropped: 0 };
  for (const r of rows) out[r.plan_status] += 1;
  return out;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** One plain sentence from the counts: "24 plan items across 12 projects: 9 in progress, 5 picked, 8 ideas, 2 live." */
export function headline(groups: PlanGroup[], label: string): string {
  const rows = groups.flatMap((g) => g.rows);
  if (rows.length === 0) return `Nothing is planned for ${label} yet.`;
  const c = countByStatus(rows);
  const parts = [
    c.in_progress > 0 ? `${fmt.count(c.in_progress)} in progress` : null,
    c.picked > 0 ? `${fmt.count(c.picked)} picked` : null,
    c.on_hold > 0 ? `${fmt.count(c.on_hold)} on hold` : null,
    c.idea > 0 ? `${fmt.count(c.idea)} ${plural(c.idea, 'idea', 'ideas')}` : null,
    c.live > 0 ? `${fmt.count(c.live)} live` : null,
    c.dropped > 0 ? `${fmt.count(c.dropped)} dropped` : null,
  ].filter(Boolean);
  const n = rows.length;
  return `${fmt.count(n)} plan ${plural(n, 'item', 'items')} across ${fmt.count(groups.length)} ${plural(groups.length, 'project', 'projects')}: ${parts.join(', ')}.`;
}

/** Rows whose title, ticket, component, owner or project holds every word typed. */
export function searchRows<T extends PlanRow>(rows: T[], query: string): T[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return rows;
  return rows.filter((r) => {
    const hay = `${r.title} ${r.issue_key ?? ''} ${r.component ?? ''} ${r.owner ?? ''} ${r.project_name ?? ''} ${r.requested_by ?? ''}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** Every row of the plan, wherever it sits, to find the one the address names (?open=). */
export function findRow(data: PlanData, id: string | null): PlanRow | null {
  if (!id) return null;
  const all = [...data.projects.flatMap((g) => g.rows), ...data.adhoc, ...data.ideation, ...data.before];
  return all.find((r) => r.id === id) ?? null;
}
